import type { CSSProperties } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { InputGroupAddon } from "../components/ui/input-group"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { cn } from "../lib/utils"

const POSITION_TYPES = new Set<CSSProperties["position"]>([
  "static",
  "relative",
  "absolute",
  "fixed",
  "sticky",
])

export function InputGroupAddonRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const lifted = liftV4Props(element)
  const position = lifted.position && typeof lifted.position === "object"
    ? lifted.position as unknown as Record<string, unknown>
    : undefined
  const positionType = position?.type
  const style: CSSProperties = {
    ...(position && typeof positionType === "string"
      && POSITION_TYPES.has(positionType as CSSProperties["position"])
      ? { position: positionType as CSSProperties["position"] }
      : {}),
    ...positionOffsets(position),
    ...(lifted.style && typeof lifted.style === "object"
      ? lifted.style as CSSProperties
      : {}),
  }
  const text = lifted.text
    ? String(resolveSymbol(lifted.text) ?? lifted.text)
    : undefined

  return (
    <InputGroupAddon
      align={(lifted.align as "inline-start" | "inline-end" | "block-start" | "block-end" | undefined)}
      className={cn(lifted.className)}
      style={Object.keys(style).length > 0 ? style : undefined}
      data-position={position ? JSON.stringify(position) : undefined}
      data-cogo-element-id={lifted.id}
      data-cogo-type={lifted.type}
    >
      {text}
      {lifted.children?.map((child) => (
        <ElementRenderer key={child.id} element={child} />
      ))}
    </InputGroupAddon>
  )
}

function positionOffsets(
  position: Record<string, unknown> | undefined,
): Pick<CSSProperties, "top" | "right" | "bottom" | "left"> {
  if (!position) return {}
  const offsets: Pick<CSSProperties, "top" | "right" | "bottom" | "left"> = {}
  for (const key of ["top", "right", "bottom", "left"] as const) {
    const value = position[key]
    if (typeof value === "string" || typeof value === "number") {
      offsets[key] = value
    }
  }
  return offsets
}
